import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'
import { toast } from 'react-toastify'

const SESSION_EXPIRED_MESSAGE = 'Tu sesión expiró, inicia sesión nuevamente'
const REFRESH_PATH = '/api/v2/login/refresh'

type RetryableConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

let refreshPromise: Promise<string> | null = null
let sessionTerminated = false

/** Clears auth and redirects to login once when refresh is no longer possible. */
function terminateSession(): Promise<never> {
  if (!sessionTerminated) {
    sessionTerminated = true
    Cookies.remove('auth')
    toast.error(SESSION_EXPIRED_MESSAGE)
    window.location.assign('/login')
  }
  return new Promise(() => {})
}

/** Runs a single refresh and returns the new access token. */
async function runRefresh(): Promise<string> {
  const response = await apiClient.post<{ token?: string }>(REFRESH_PATH)
  const token = response.data.token
  if (token === undefined || token === '') {
    return terminateSession()
  }
  Cookies.set('auth', token)
  return token
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API,
  withCredentials: true
})

apiClient.interceptors.request.use((config) => {
  const token = Cookies.get('auth')
  if (token !== undefined && token !== '') {
    const headers = axios.AxiosHeaders.from(config.headers)
    headers.set('Authorization', `Bearer ${token}`)
    config.headers = headers
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code?: string }>) => {
    const status = error.response?.status
    const code = error.response?.data?.code
    const config = error.config as RetryableConfig | undefined

    if (status !== 401 || code !== 'ACCESS_TOKEN_EXPIRED' || config === undefined) {
      return Promise.reject(error)
    }

    const requestUrl = config.url ?? ''
    if (config._retry === true || requestUrl.includes(REFRESH_PATH)) {
      return terminateSession()
    }

    config._retry = true

    if (refreshPromise === null) {
      refreshPromise = runRefresh().finally(() => {
        refreshPromise = null
      })
    }

    try {
      await refreshPromise
      return apiClient(config)
    } catch {
      return terminateSession()
    }
  }
)

export default apiClient
